import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Droplets, Wifi, WifiOff, Zap, Clock, Leaf, CloudRain, 
  Thermometer, Link2, ExternalLink, CheckCircle, Sparkles,
  Play, Square, RefreshCw, Settings, AlertTriangle, BarChart3, Gauge,
  Crown, Layers, ShieldCheck, Info
} from 'lucide-react';
import { DepartmentNav } from '../components/DepartmentNav';
import { PageWhy } from '../components/PageWhy';
import { AgronomyCalendarStrip } from '../components/AgronomyCalendarStrip';
import { QuickTestRunCard } from '../components/QuickTestRunCard';
import { RachioScheduleBuilder } from '../components/RachioScheduleBuilder';
import { OptimalWindowCard } from '../components/OptimalWindowCard';
import { ZoneDeltaCard } from '../components/ZoneDeltaCard';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

import { API_URL } from '../config/api';

// Feature Card — with optional BONUS variant for the standout differentiator
// and an optional caveat shown as an italic line + (i) icon with browser tooltip.
const FeatureCard = ({ icon: Icon, title, description, color = "emerald", bonus = false, caveat = null, caveatHref = null }) => (
  <div
    className={
      bonus
        ? "relative flex items-start gap-4 p-5 rounded-lg bg-gradient-to-br from-emerald-50 to-amber-50 dark:from-emerald-950/40 dark:to-amber-950/30 border-2 border-emerald-400 dark:border-emerald-600 shadow-sm"
        : "flex items-start gap-4 p-4 rounded-lg bg-muted/30 border border-border/50"
    }
    data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
  >
    {bonus && (
      <div
        className="absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-white shadow"
        data-testid="feature-card-bonus-badge"
      >
        <Crown className="w-3 h-3" /> Bonus · TurfPulse Only
      </div>
    )}
    <div className={`p-2 rounded-lg bg-${color}-100 dark:bg-${color}-900/30 shrink-0`}>
      <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <h4 className="font-semibold text-foreground">{title}</h4>
        {caveat && (
          <span
            title={caveat}
            aria-label={caveat}
            className="inline-flex shrink-0"
            data-testid="feature-card-info-icon"
          >
            <Info className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 cursor-help" />
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{description}</p>
      {caveat && (
        <p
          className="mt-2 text-xs italic text-amber-700 dark:text-amber-300 leading-relaxed"
          data-testid="feature-card-caveat"
        >
          <span className="font-semibold not-italic">Heads up:</span> {caveat}
          {caveatHref && (
            <>
              {' '}
              <Link
                to={caveatHref}
                className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100 not-italic"
                data-testid="feature-card-caveat-link"
              >
                Log an application →
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  </div>
);

// Supported Controller Card
const ControllerCard = ({ name, logo, status, description, onConnect }) => (
  <Card className="relative overflow-hidden">
    {status === "coming_soon" && (
      <div className="absolute top-2 right-2">
        <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
      </div>
    )}
    {status === "available" && (
      <div className="absolute top-2 right-2">
        <Badge className="bg-emerald-500 text-xs">Available</Badge>
      </div>
    )}
    <CardContent className="pt-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
          {logo}
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{name}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {status === "available" && onConnect && (
        <Button 
          onClick={onConnect} 
          className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700"
          size="sm"
        >
          <Link2 className="w-4 h-4 mr-2" />
          Connect
        </Button>
      )}
      {status === "coming_soon" && (
        <Button disabled className="w-full mt-2" size="sm" variant="outline">
          <Clock className="w-4 h-4 mr-2" />
          Coming Soon
        </Button>
      )}
    </CardContent>
  </Card>
);

export const IrrigationPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [loading, setLoading] = useState(true);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [usageSummary, setUsageSummary] = useState(null);
  const [recentUsage, setRecentUsage] = useState([]);
  const [nozzleTypes, setNozzleTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [showZoneConfigDialog, setShowZoneConfigDialog] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneConfig, setZoneConfig] = useState({
    nozzle_type: 'fixed_spray',
    square_footage: 1000,
    precipitation_rate: 1.5
  });
  
  // Manual sprinkler schedule state
  const [sprinklerZones, setSprinklerZones] = useState([]);
  const [scheduleStats, setScheduleStats] = useState({
    avg_weekly_inches_per_zone: 0,
    target_weekly_inches: 1.0,
    under_zones_count: 0,
    on_target_zones_count: 0,
    over_zones_count: 0,
    overall_status: 'empty',
  });
  const [showAddZone, setShowAddZone] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    zone_name: '', run_time_minutes: 10, days: [], start_time: '06:00',
    head_type: 'spray', precipitation_rate_inhr: 1.5, yard_zone_id: '', yard_zone_name: '', notes: ''
  });
  const [savingZone, setSavingZone] = useState(false);
  const [yardZones, setYardZones] = useState([]);

  const HEAD_TYPES = [
    { value: 'spray', label: 'Fixed Spray', rate: 1.5 },
    { value: 'rotary', label: 'Rotary/Rotor', rate: 0.7 },
    { value: 'drip', label: 'Drip', rate: 0.5 },
    { value: 'impact', label: 'Impact', rate: 1.0 },
    { value: 'mp_rotator', label: 'MP Rotator', rate: 0.4 },
  ];
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const fetchSprinklerSchedule = async () => {
    try {
      const [schedRes, zonesRes] = await Promise.all([
        axios.get(`${API_URL}/api/irrigation/sprinkler-schedule`, { withCredentials: true }),
        axios.get(`${API_URL}/api/property/zones`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSprinklerZones(schedRes.data.zones);
      setScheduleStats({
        avg_weekly_inches_per_zone: schedRes.data.avg_weekly_inches_per_zone || 0,
        target_weekly_inches: schedRes.data.target_weekly_inches || 1.0,
        under_zones_count: schedRes.data.under_zones_count || 0,
        on_target_zones_count: schedRes.data.on_target_zones_count || 0,
        over_zones_count: schedRes.data.over_zones_count || 0,
        overall_status: schedRes.data.overall_status || 'empty',
      });
      setYardZones(zonesRes.data || []);
    } catch {}
  };

  const handleSaveZone = async () => {
    if (!zoneForm.zone_name.trim()) { toast.error('Zone name required'); return; }
    setSavingZone(true);
    try {
      const payload = { ...zoneForm, zone_number: editingZone ? editingZone.zone_number : sprinklerZones.length + 1 };
      if (editingZone) payload.id = editingZone.id;
      await axios.post(`${API_URL}/api/irrigation/sprinkler-schedule`, payload, { withCredentials: true });
      toast.success(editingZone ? 'Zone updated' : 'Zone added');
      setShowAddZone(false);
      setEditingZone(null);
      setZoneForm({ zone_name: '', run_time_minutes: 10, days: [], start_time: '06:00', head_type: 'spray', precipitation_rate_inhr: 1.5, notes: '' });
      fetchSprinklerSchedule();
    } catch { toast.error('Failed to save zone'); }
    finally { setSavingZone(false); }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Delete this zone?')) return;
    try {
      await axios.delete(`${API_URL}/api/irrigation/sprinkler-schedule/${zoneId}`, { withCredentials: true });
      toast.success('Zone deleted');
      fetchSprinklerSchedule();
    } catch { toast.error('Failed to delete'); }
  };

  const openEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      zone_name: zone.zone_name, run_time_minutes: zone.run_time_minutes,
      days: zone.days, start_time: zone.start_time, head_type: zone.head_type,
      precipitation_rate_inhr: zone.precipitation_rate_inhr,
      yard_zone_id: zone.yard_zone_id || '', yard_zone_name: zone.yard_zone_name || '',
      notes: zone.notes || ''
    });
    setShowAddZone(true);
  };

  const toggleDay = (day) => {
    setZoneForm(prev => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter(d => d !== day) : [...prev.days, day]
    }));
  };

  useEffect(() => {
    fetchStatus();
    fetchNozzleTypes();
    fetchSprinklerSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (integrationStatus?.connected) {
      fetchUsageData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationStatus]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/irrigation/status`, { withCredentials: true });
      setIntegrationStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch irrigation status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const [summaryRes, recentRes] = await Promise.all([
        axios.get(`${API_URL}/api/irrigation/usage/summary?days=30`, { withCredentials: true }),
        axios.get(`${API_URL}/api/irrigation/usage/recent?limit=10`, { withCredentials: true })
      ]);
      setUsageSummary(summaryRes.data);
      setRecentUsage(recentRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
    }
  };

  const fetchNozzleTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/irrigation/nozzle-types`);
      setNozzleTypes(res.data.nozzle_types || []);
    } catch (error) {
      console.error('Failed to fetch nozzle types:', error);
    }
  };

  const handleConfigureZone = (zone) => {
    setSelectedZone(zone);
    setZoneConfig({
      nozzle_type: 'fixed_spray',
      square_footage: 1000,
      precipitation_rate: 1.5
    });
    setShowZoneConfigDialog(true);
  };

  const handleSaveZoneConfig = async () => {
    if (!selectedZone) return;
    
    try {
      await axios.post(`${API_URL}/api/irrigation/zones/${selectedZone.id}/config`, {
        zone_id: selectedZone.id,
        ...zoneConfig
      }, { withCredentials: true });
      toast.success(`Zone "${selectedZone.name}" configured!`);
      setShowZoneConfigDialog(false);
    } catch (error) {
      toast.error('Failed to save zone configuration');
    }
  };

  const handleConnectRachio = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter your Rachio API key');
      return;
    }

    setConnecting(true);
    try {
      const res = await axios.post(`${API_URL}/api/irrigation/rachio/connect`, {
        api_key: apiKey
      }, { withCredentials: true });
      
      toast.success(res.data.message);
      setShowConnectDialog(false);
      setApiKey('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Rachio');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your irrigation system?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/irrigation/rachio/disconnect`, { withCredentials: true });
      toast.success('Irrigation system disconnected');
      fetchStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  // What TurfPulse actually does for irrigation. Probe-driven, sensor-gated,
  // agronomically opinionated. The brain on top of the Rachio — not a
  // calendar timer with a pretty UI.
  const upcomingFeatures = [
    {
      icon: Gauge,
      title: "Probe-Driven Runtime",
      description: "Your soil moisture probe tells us how much water the lawn actually needs — we convert that to exact minutes per zone. No more 'water Tuesdays for 30 min' guesses.",
      color: "emerald"
    },
    {
      icon: ShieldCheck,
      title: "Sensor-Gated Skip Stack",
      description: "Rain, freeze, wind, dewpoint, MAD, and city blackout windows — all wired to your probes, not just a forecast. We skip when the probes agree, fire when they disagree.",
      color: "cyan"
    },
    {
      icon: BarChart3,
      title: "Per-Zone Runtime, Calculated Live",
      description: "Front zone reads 22% SVWC, back zone reads 28%? Front gets 60 min tonight, back gets 12. Same total water — distributed where the soil asks for it.",
      color: "blue"
    },
    {
      icon: AlertTriangle,
      title: "TAMU 12% Drought-Floor Override",
      description: "When your DRY-zone probe drops below 12% SVWC (Wherley/TAMU AgriLife threshold), we fire even if the forecast says 'skip.' Dead grass doesn't care that rain was supposed to come.",
      color: "amber"
    },
    {
      icon: Leaf,
      title: "Auto-Pause After Applications",
      description: "We know your PGR / fert / herbicide schedule. We pause irrigation 24–48 hrs after so your application actually does what you paid for.",
      color: "emerald",
      bonus: true,
      caveat: "Auto-pause only fires when you log the application in TurfPulse first — we can't pause for an application we don't know about.",
      caveatHref: "/pgr"
    },
    {
      icon: Sparkles,
      title: "Smart Split Schedules",
      description: "Tue/Thu schedule auto-splits 29% / 71% based on the dry-gap interval before the next fire. Deeper soak before the longer drought stretch grows deeper roots — Rachio doesn't do this.",
      color: "violet"
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="irrigation-page">
      <DepartmentNav />
      
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <Droplets className="w-8 h-8 text-blue-500" />
                Smart Irrigation
              </h1>
              <Badge className="bg-amber-500 text-white">
                <Sparkles className="w-3 h-3 mr-1" />
                Beta
              </Badge>
            </div>
            <PageWhy testId="irrigation-page-why">See exactly whether you're over- or under-watering — based on your sprinkler heads, your zones, and real weather. Dial in the right amount or connect a smart controller for automatic adjustments.</PageWhy>
          </div>
        </div>

        {/* ====== "OUT OF THE BOX" DIFFERENTIATOR BANNER ======
            The single biggest moat TurfPulse has over a vanilla Rachio
            install: per-day variable runtimes that match the actual dry-gap
            interval. We surface it here, at the top of /irrigation, so
            customers see the value the moment they land on this page.
            Theme-aware (light + dark) — uses semantic tokens so contrast
            stays WCAG-AA on both modes. */}
        <div
          className="mb-8 relative overflow-hidden rounded-2xl border-2 border-emerald-500/40 bg-card p-6 shadow-sm"
          data-testid="smart-split-hero-banner"
        >
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-bold">
                  ✨ TurfPulse exclusive
                </span>
                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 text-[10px]">
                  Paid feature
                </Badge>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground leading-snug">
                We thought outside the box — so your roots can grow deeper.
              </h2>
              <p className="text-sm text-foreground/80 mt-2 leading-relaxed">
                Most cities allow watering only 2 days a week (e.g., <strong className="text-foreground">Tue + Thu</strong>) — but a Rachio out of the box runs the <strong className="text-foreground">same duration</strong> on each day. That ignores reality: there's a <strong className="text-foreground">2-day dry gap</strong> between Tue→Thu, but a <strong className="text-foreground">5-day dry gap</strong> between Thu→next Tue.
              </p>
              <p className="text-sm text-foreground/80 mt-2 leading-relaxed">
                <strong className="text-emerald-700 dark:text-emerald-400">TurfPulse Smart Split</strong> automatically runs <strong className="text-foreground">lighter on Tuesday</strong> (small top-up) and <strong className="text-foreground">deeper on Thursday</strong> (long-soak before the long dry stretch). Same total weekly water — redistributed so your <strong className="text-foreground">roots train downward</strong> chasing moisture. Tighter turf, better drought tolerance, less stress in August.
              </p>
              <p className="text-xs text-muted-foreground mt-3 italic">
                Your controller is the bridge — TurfPulse is the brain on top.
              </p>
            </div>
            {/* Visual proof: the math, side-by-side */}
            <div className="flex gap-3 md:flex-shrink-0">
              <div className="border border-border bg-muted/40 rounded-lg p-3 w-32 sm:w-36" data-testid="hero-proof-rachio">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">Rachio default</div>
                <div className="mt-2 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Tue</span>
                    <span className="text-foreground font-mono">15 min</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Thu</span>
                    <span className="text-foreground font-mono">15 min</span>
                  </div>
                  <div className="text-[9px] text-muted-foreground italic pt-1 border-t border-border/60">
                    same / same
                  </div>
                </div>
              </div>
              <div className="border-2 border-emerald-500/60 bg-emerald-500/10 rounded-lg p-3 w-32 sm:w-36 shadow-[0_0_20px_rgba(16,185,129,0.15)]" data-testid="hero-proof-turfpulse">
                <div className="text-[9px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">TurfPulse</div>
                <div className="mt-2 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Tue</span>
                    <span className="text-amber-700 dark:text-amber-300 font-mono">9 min</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Thu</span>
                    <span className="text-emerald-700 dark:text-emerald-300 font-mono font-bold">21 min</span>
                  </div>
                  <div className="text-[9px] text-emerald-700/90 dark:text-emerald-500/80 italic pt-1 border-t border-emerald-500/30">
                    light → deep ✨
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ====== WATERING WINDOW · LIVE (Decision Engine v2) ====== */}
        <OptimalWindowCard />

        {/* ====== PER-ZONE DELTA vs target (closes the observation→action loop) ====== */}
        <ZoneDeltaCard />

        {/* ====== SCHEDULES (Rachio mirror — full builder) ====== */}
        <RachioScheduleBuilder />

        {/* ====== 14-DAY AGRONOMY CALENDAR ====== */}
        <AgronomyCalendarStrip />

        {/* ====== QUICK TEST RUN — walk-the-yard sprinkler check ====== */}
        <QuickTestRunCard />

        {/* Add/Edit Zone Dialog */}
        <Dialog open={showAddZone} onOpenChange={setShowAddZone}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingZone ? 'Edit Zone' : 'Add Sprinkler Zone'}</DialogTitle>
              <DialogDescription>Enter your sprinkler settings for this zone</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Zone Name</Label>
                <Input data-testid="zone-name-input" placeholder="e.g., Front Yard, Back Left" value={zoneForm.zone_name} onChange={e => setZoneForm(p => ({ ...p, zone_name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Run Time (minutes)</Label>
                  <Input
                    data-testid="zone-runtime-input"
                    type="number"
                    min="1"
                    max="120"
                    value={zoneForm.run_time_minutes}
                    onChange={e => {
                      const v = e.target.value;
                      // Allow empty string during typing so backspace works.
                      // We snap to a valid range on blur, not on every keystroke.
                      setZoneForm(p => ({
                        ...p,
                        run_time_minutes: v === '' ? '' : (parseInt(v, 10) || 0),
                      }));
                    }}
                    onBlur={e => {
                      const n = parseInt(e.target.value, 10);
                      if (isNaN(n) || n < 1) {
                        setZoneForm(p => ({ ...p, run_time_minutes: 1 }));
                      } else if (n > 120) {
                        setZoneForm(p => ({ ...p, run_time_minutes: 120 }));
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input data-testid="zone-starttime-input" type="time" value={zoneForm.start_time} onChange={e => setZoneForm(p => ({ ...p, start_time: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Watering Days</Label>
                <div className="flex gap-1.5">
                  {DAYS.map(day => (
                    <button key={day} type="button" onClick={() => toggleDay(day)}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        zoneForm.days.includes(day) ? 'bg-blue-500 text-white border-blue-500' : 'bg-background border-border hover:border-blue-300'
                      }`} data-testid={`day-toggle-${day}`}>{day}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Head Type</Label>
                <Select value={zoneForm.head_type} onValueChange={v => {
                  const ht = HEAD_TYPES.find(h => h.value === v);
                  setZoneForm(p => ({ ...p, head_type: v, precipitation_rate_inhr: ht?.rate || 1.5 }));
                }}>
                  <SelectTrigger data-testid="head-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HEAD_TYPES.map(h => <SelectItem key={h.value} value={h.value}>{h.label} (~{h.rate} in/hr)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input data-testid="zone-notes-input" placeholder="e.g., Broken head near driveway" value={zoneForm.notes} onChange={e => setZoneForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
              {yardZones.length > 0 && (
                <div className="space-y-2">
                  <Label>Yard Zone (links to your property)</Label>
                  <Select value={zoneForm.yard_zone_id} onValueChange={v => {
                    const yz = yardZones.find(z => z.id === v);
                    setZoneForm(p => ({ ...p, yard_zone_id: v, yard_zone_name: yz?.name || '' }));
                  }}>
                    <SelectTrigger data-testid="yard-zone-select"><SelectValue placeholder="Link to yard zone (optional)" /></SelectTrigger>
                    <SelectContent>
                      {yardZones.map(yz => (
                        <SelectItem key={yz.id} value={yz.id}>
                          {yz.name}{yz.orientation ? ` (${yz.orientation})` : ''}{yz.sun_exposure ? ` — ${yz.sun_exposure}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Multiple irrigation zones can map to the same yard zone</p>
                </div>
              )}
              {/* Preview */}
              {zoneForm.days.length > 0 && (
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Est. weekly output: <strong>{((zoneForm.run_time_minutes / 60) * zoneForm.precipitation_rate_inhr * zoneForm.days.length).toFixed(2)}"</strong>
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                    {zoneForm.run_time_minutes} min x {zoneForm.days.length} days x {zoneForm.precipitation_rate_inhr} in/hr
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button data-testid="save-zone-btn" onClick={handleSaveZone} disabled={savingZone} className="flex-1">
                  {savingZone ? 'Saving...' : editingZone ? 'Update Zone' : 'Add Zone'}
                </Button>
                <Button variant="outline" onClick={() => setShowAddZone(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : integrationStatus?.connected ? (
          /* Connected State */
          <div className="space-y-6">
            {/* Connection Status */}
            <Card className="border-l-4 border-l-emerald-500">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Wifi className="w-5 h-5 text-emerald-500" />
                      Connected to {integrationStatus.provider === 'rachio' ? 'Rachio' : integrationStatus.provider}
                    </CardTitle>
                    <CardDescription>
                      Last synced: {integrationStatus.last_sync ? new Date(integrationStatus.last_sync).toLocaleString() : 'Never'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchUsageData(); }}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDisconnect} className="text-red-500 hover:text-red-600">
                      <WifiOff className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Tabs for Zones and Usage */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="overview">
                  <Droplets className="w-4 h-4 mr-2" />
                  Zones
                </TabsTrigger>
                <TabsTrigger value="usage">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Usage
                </TabsTrigger>
                <TabsTrigger value="settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </TabsTrigger>
              </TabsList>

              {/* Zones Tab */}
              <TabsContent value="overview" className="space-y-4">
                {integrationStatus.devices?.map((device) => (
                  <Card key={device.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{device.name}</span>
                        <Badge variant={device.status === 'ONLINE' ? 'default' : 'secondary'}>
                          {device.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription>{device.model} • {device.zones?.length || 0} zones</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {device.zones?.filter(z => z.enabled).map((zone) => (
                          <div 
                            key={zone.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border hover:border-emerald-500 cursor-pointer transition-colors"
                            onClick={() => handleConfigureZone(zone)}
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-sm font-medium text-blue-600">
                                {zone.zone_number}
                              </div>
                              <span className="text-sm font-medium truncate">{zone.name}</span>
                            </div>
                            <Settings className="w-4 h-4 text-muted-foreground" />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        Click a zone to configure nozzle type and square footage for accurate water estimates
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              {/* Usage Tab */}
              <TabsContent value="usage" className="space-y-4">
                {/* Usage Summary Cards */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                          <Droplets className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {usageSummary?.totals?.gallons?.toLocaleString() || 0}
                          </div>
                          <div className="text-sm text-muted-foreground">Est. Gallons (30d)</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                          <Clock className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {usageSummary?.totals?.minutes?.toLocaleString() || 0}
                          </div>
                          <div className="text-sm text-muted-foreground">Minutes (30d)</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-lg bg-violet-100 dark:bg-violet-900/30">
                          <Gauge className="w-6 h-6 text-violet-600" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {usageSummary?.totals?.runs || 0}
                          </div>
                          <div className="text-sm text-muted-foreground">Watering Runs</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Usage by Zone */}
                {usageSummary?.by_zone?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Usage by Zone</CardTitle>
                      <CardDescription>Water consumption per zone (last 30 days)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {usageSummary.by_zone.map((zone, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                            <div className="font-medium">{zone.zone_name || 'Unknown Zone'}</div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-blue-600 font-medium">{zone.gallons} gal</span>
                              <span className="text-muted-foreground">{zone.minutes} min</span>
                              <span className="text-muted-foreground">{zone.runs} runs</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Activity */}
                {recentUsage.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Recent Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {recentUsage.map((event, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <div className="font-medium text-sm">{event.zone_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(event.created_at).toLocaleString()}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-blue-600">{event.estimated_gallons} gal</div>
                              <div className="text-xs text-muted-foreground">{event.duration_minutes} min</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {(!usageSummary?.by_zone?.length && !recentUsage.length) && (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">No Usage Data Yet</h3>
                      <p className="text-muted-foreground">
                        Water usage will be tracked when zones run. Configure your zones first for accurate estimates.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Zone Configuration</CardTitle>
                    <CardDescription>
                      Configure each zone's nozzle type and square footage for accurate water usage estimates
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {nozzleTypes.map((nozzle) => (
                        <div key={nozzle.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                          <div>
                            <div className="font-medium">{nozzle.name}</div>
                            <div className="text-sm text-muted-foreground">{nozzle.description}</div>
                          </div>
                          <Badge variant="outline">{nozzle.precipitation_rate} in/hr</Badge>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                      <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">How We Calculate Gallons</h4>
                      <p className="text-sm text-blue-600 dark:text-blue-500">
                        <strong>Gallons</strong> = Precipitation Rate (in/hr) × Square Footage × Run Time (hrs) × 0.623
                      </p>
                      <p className="text-xs text-blue-500 dark:text-blue-600 mt-1">
                        0.623 = gallons per square foot per inch of water
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          /* Not Connected State - Coming Soon UI */
          <div className="space-y-8">
            {/* Hero Section */}
            <Card className="overflow-hidden">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-emerald-500/10" />
                <CardContent className="relative py-12 text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 mb-6">
                    <Droplets className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Smart Irrigation Integration
                  </h2>
                  <p className="text-muted-foreground max-w-lg mx-auto mb-6">
                    Connect your smart sprinkler system to TurfPulse for GDD-powered watering schedules 
                    that know exactly when your lawn needs water.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <CheckCircle className="w-4 h-4" />
                    <span>Rachio integration available now</span>
                  </div>
                </CardContent>
              </div>
            </Card>

            {/* Supported Controllers */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Supported Controllers</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <ControllerCard 
                  name="Rachio"
                  logo="R"
                  status="available"
                  description="Gen 3 & Gen 2"
                  onConnect={() => setShowConnectDialog(true)}
                />
                <ControllerCard 
                  name="Hunter Hydrawise"
                  logo="H"
                  status="coming_soon"
                  description="HC & Pro-HC"
                />
                <ControllerCard 
                  name="Rain Bird"
                  logo="RB"
                  status="coming_soon"
                  description="ST8 WiFi"
                />
                <ControllerCard 
                  name="Orbit B-hyve"
                  logo="O"
                  status="coming_soon"
                  description="XR & Smart"
                />
              </div>
            </div>

            {/* What TurfPulse actually does for your irrigation */}
            <div data-testid="irrigation-feature-grid">
              <h3 className="text-lg font-semibold text-foreground mb-1">What TurfPulse adds on top of your controller</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Your controller is the bridge — TurfPulse is the brain on top. Here's what we do that a stock smart controller doesn't.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {upcomingFeatures.map((feature, idx) => (
                  <FeatureCard key={idx} {...feature} />
                ))}
              </div>
            </div>

            {/* How It Works */}
            <Card data-testid="irrigation-how-it-works">
              <CardHeader>
                <CardTitle>How It Works</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 sm:grid-cols-3">
                  <div className="text-center" data-testid="how-it-works-step-1">
                    <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-blue-600">1</span>
                    </div>
                    <h4 className="font-medium">Connect Your Controller</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Link your Rachio with the API key from the Rachio app — TurfPulse never touches your password
                    </p>
                  </div>
                  <div className="text-center" data-testid="how-it-works-step-2">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-emerald-600">2</span>
                    </div>
                    <h4 className="font-medium">Pair Your Zones to Probes</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Map each Rachio zone to a soil probe (DRY / AVG / WET) and TurfPulse starts polling every 15 min
                    </p>
                  </div>
                  <div className="text-center" data-testid="how-it-works-step-3">
                    <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-3">
                      <span className="text-xl font-bold text-amber-600">3</span>
                    </div>
                    <h4 className="font-medium">Probe-Driven Fires, with Receipts</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Predawn fires only when probes + skip-gates agree — every fire and skip logged with the snapshot that drove it
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Get API Key Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  How to Get Your Rachio API Key
                </CardTitle>
                <CardDescription>
                  Works with Rachio Gen 2, Gen 3, and Gen 3e controllers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Method 1: Mobile App */}
                <div>
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-bold text-blue-600">1</span>
                    From the Rachio Mobile App
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>Open the <strong>Rachio app</strong> on your phone (iOS or Android)</li>
                    <li>Tap the <strong>Account</strong> icon in the top-right corner</li>
                    <li>Scroll to the bottom of the Account screen</li>
                    <li>Your <strong>API key</strong> is displayed at the bottom — tap to copy</li>
                  </ol>
                </div>

                {/* Method 2: Web App */}
                <div>
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-xs font-bold text-emerald-600">2</span>
                    From the Rachio Web App (Recommended)
                  </h4>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground ml-8">
                    <li>Go to <strong><a href="https://app.rach.io" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">app.rach.io</a></strong> and log in</li>
                    <li>Click <strong>Settings</strong> (gear icon)</li>
                    <li>Click <strong>"Get API Key"</strong></li>
                    <li>Copy the key from the dialog — it looks like: <code className="text-xs bg-muted px-1 py-0.5 rounded">xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx</code></li>
                  </ol>
                </div>

                {/* What it does */}
                <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <h4 className="font-medium text-foreground mb-2">What TurfPulse Does With Your Key</h4>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Reads your controller zones and run history</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Tracks actual water usage (gallons per zone)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span>Syncs zone names and configurations</span>
                    </li>
                  </ul>
                </div>

                {/* Rate limits & security note */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    <span>Your API key is stored securely and encrypted. Only used to communicate with Rachio's cloud API.</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    <span>Rachio allows 1,700 API calls/day. TurfPulse uses minimal calls for sync and monitoring.</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                    <span>To rotate your key for security, repeat the "Get API Key" step — this invalidates the old one.</span>
                  </div>
                </div>

                <Button onClick={() => setShowConnectDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-rachio-btn">
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Rachio Now
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Connect Dialog */}
        <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                  R
                </div>
                Connect Rachio
              </DialogTitle>
              <DialogDescription>
                Enter your Rachio API key to connect your smart sprinkler system
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  data-testid="rachio-api-key-input"
                />
                <p className="text-xs text-muted-foreground">
                  Find this in Rachio App → Account → Get API Key
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleConnectRachio} 
                  disabled={connecting}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {connecting ? 'Connecting...' : 'Connect'}
                </Button>
                <Button variant="outline" onClick={() => setShowConnectDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Zone Configuration Dialog */}
        <Dialog open={showZoneConfigDialog} onOpenChange={setShowZoneConfigDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-emerald-500" />
                Configure Zone: {selectedZone?.name}
              </DialogTitle>
              <DialogDescription>
                Set nozzle type and square footage for accurate water usage estimates
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nozzle Type</Label>
                <Select 
                  value={zoneConfig.nozzle_type} 
                  onValueChange={(v) => {
                    const nozzle = nozzleTypes.find(n => n.id === v);
                    setZoneConfig(prev => ({
                      ...prev,
                      nozzle_type: v,
                      precipitation_rate: nozzle?.precipitation_rate || 1.5
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {nozzleTypes.map((nozzle) => (
                      <SelectItem key={nozzle.id} value={nozzle.id}>
                        {nozzle.name} ({nozzle.precipitation_rate} in/hr)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sq_ft">Square Footage</Label>
                <Input
                  id="sq_ft"
                  type="number"
                  min="100"
                  max="50000"
                  value={zoneConfig.square_footage}
                  onChange={(e) => {
                    const v = e.target.value;
                    setZoneConfig(prev => ({
                      ...prev,
                      square_footage: v === '' ? '' : (parseInt(v, 10) || 0),
                    }));
                  }}
                  onBlur={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (isNaN(n) || n < 100) {
                      setZoneConfig(prev => ({ ...prev, square_footage: 1000 }));
                    } else if (n > 50000) {
                      setZoneConfig(prev => ({ ...prev, square_footage: 50000 }));
                    }
                  }}
                />
                <p className="text-xs text-muted-foreground">Approximate area this zone covers</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="precip_rate">Precipitation Rate (in/hr)</Label>
                <Input
                  id="precip_rate"
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="5"
                  value={zoneConfig.precipitation_rate}
                  onChange={(e) => setZoneConfig(prev => ({ ...prev, precipitation_rate: parseFloat(e.target.value) || 1.5 }))}
                />
                <p className="text-xs text-muted-foreground">Auto-set based on nozzle type, or customize</p>
              </div>
              
              {/* Estimate Preview */}
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                <div className="text-sm text-emerald-700 dark:text-emerald-400">
                  <strong>Estimated usage:</strong>
                </div>
                <div className="text-lg font-bold text-emerald-600 mt-1">
                  {(zoneConfig.precipitation_rate * zoneConfig.square_footage * 0.623 / 60).toFixed(1)} GPM
                </div>
                <div className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">
                  ~{(zoneConfig.precipitation_rate * zoneConfig.square_footage * 0.623 * (10/60)).toFixed(0)} gallons for a 10-minute run
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveZoneConfig}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  Save Configuration
                </Button>
                <Button variant="outline" onClick={() => setShowZoneConfigDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default IrrigationPage;

/**
 * RecurringSmartSchedulesCard — surfaces server-side recurring schedules
 * (saved via /water-budget) on the main /irrigation page so users have
 * one unified view of their watering plan. Shows pause/resume/delete inline.
 *
 * Self-renders an empty-state CTA when the user has zero saved schedules
 * so the section is always present and discoverable.
 */
const RecurringSmartSchedulesCard = () => {
  const [schedules, setSchedules] = useState(null);
  const [busy, setBusy] = useState({});
  const [guardian, setGuardian] = useState({ mode: 'advisory_only', note: '', autoFire: false });
  const [breakdowns, setBreakdowns] = useState({}); // schedule_id → breakdown rows

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/irrigation/schedules`, { withCredentials: true });
      const list = r.data?.schedules || [];
      setSchedules(list);
      setGuardian({
        mode: r.data?.guardian_mode || 'advisory_only',
        note: r.data?.guardian_note || '',
        autoFire: !!r.data?.auto_fire_enabled,
      });
      // Fetch per-day breakdown for each schedule in parallel
      const results = await Promise.allSettled(
        list.map((s) =>
          axios.get(`${API_URL}/api/irrigation/schedules/${s.id}/breakdown`, { withCredentials: true })
            .then((x) => [s.id, x.data?.breakdown || []])
        )
      );
      const map = {};
      for (const r2 of results) {
        if (r2.status === 'fulfilled') {
          const [id, rows] = r2.value;
          map[id] = rows;
        }
      }
      setBreakdowns(map);
    } catch {
      setSchedules([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (s) => {
    setBusy((b) => ({ ...b, [s.id]: true }));
    try {
      await axios.put(
        `${API_URL}/api/irrigation/schedules/${s.id}`,
        { enabled: !s.enabled },
        { withCredentials: true },
      );
      toast.success(`${s.name} ${s.enabled ? 'paused' : 'resumed'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusy((b) => ({ ...b, [s.id]: false }));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete schedule "${s.name}"? This cannot be undone.`)) return;
    setBusy((b) => ({ ...b, [s.id]: true }));
    try {
      await axios.delete(`${API_URL}/api/irrigation/schedules/${s.id}`, { withCredentials: true });
      toast.success(`Deleted "${s.name}"`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy((b) => ({ ...b, [s.id]: false }));
    }
  };

  if (schedules === null) return null;

  const dayLabel = (d) => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d] || '?';

  return (
    <Card className="mb-8" data-testid="recurring-smart-schedules-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Zap className="w-5 h-5 text-emerald-500" />
              TurfPulse Smart Recurring Schedules
              {guardian.autoFire ? (
                <Badge
                  className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 ml-1"
                  data-testid="guardian-mode-badge"
                  title="Sensors connected — auto-fire enabled"
                >
                  ✅ Auto-fire enabled
                </Badge>
              ) : (
                <Badge
                  className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40 ml-1"
                  data-testid="guardian-mode-badge"
                  title="No soil sensors connected — TurfPulse will NOT auto-fire on theory alone"
                >
                  🛡️ Advisory only
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Server-side schedules from the Water Budget. Auto-skips on rain forecast and city blackouts.
            </CardDescription>
          </div>
          <Link
            to="/water-budget"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-md whitespace-nowrap"
            data-testid="recurring-create-link"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {schedules.length === 0 ? 'Create from Water Budget' : 'New schedule'}
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {/* TurfPulse Guardian banner — visible whenever sensors aren't yet
            connected. Re-frames "missing sensors" as a deliberate safety
            posture, not a feature gap. */}
        {!guardian.autoFire && (
          <div
            className="mb-4 rounded-lg border-2 border-amber-500/40 bg-amber-50 dark:bg-amber-950/20 p-3 sm:p-4"
            data-testid="guardian-advisory-banner"
          >
            <div className="flex items-start gap-3">
              <div className="text-2xl leading-none mt-0.5">🛡️</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-1">
                  TurfPulse Guardian: advisory mode
                </div>
                <p className="text-xs sm:text-sm text-amber-800/90 dark:text-amber-200/85 leading-relaxed">
                  {guardian.note || (
                    <>
                      Auto-fire requires connected Ecowitt soil moisture sensors.
                      TurfPulse won't fire on theoretical math alone — other smart
                      controllers will, we won't. Connect sensors to enable, or
                      run schedules manually in your Rachio app.
                    </>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                  <a
                    href="/byos"
                    className="underline text-amber-900 dark:text-amber-200 hover:text-amber-700 dark:hover:text-amber-100 font-medium"
                  >
                    Connect Ecowitt sensors →
                  </a>
                  <span className="text-amber-700/60 dark:text-amber-300/60">·</span>
                  <span className="text-amber-700/80 dark:text-amber-300/80">
                    No fines, no flooding, no theory-only firing.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {schedules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="recurring-empty-state">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No recurring schedules yet.</p>
            <p className="text-xs mt-1">
              Build one from the <Link to="/water-budget" className="text-emerald-600 underline">Water Budget</Link> —
              TurfPulse will auto-fire your Rachio on the days &amp; time you pick.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {schedules.map((s) => {
              const totalMin = (s.zones || []).reduce((acc, z) => acc + (z.duration_minutes || 0), 0);
              const next = s.next_fire_at
                ? new Date(s.next_fire_at).toLocaleString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })
                : '—';
              return (
                <div
                  key={s.id}
                  className="border border-border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`recurring-row-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{s.name}</span>
                      {!s.enabled && (
                        <Badge variant="secondary" className="text-[9px] uppercase bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                          Paused
                        </Badge>
                      )}
                      {s.enabled && (
                        <Badge className="text-[9px] uppercase bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                          Active
                        </Badge>
                      )}
                      {s.split_strategy === 'smart_gap' && (
                        <Badge
                          className="text-[9px] uppercase bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40"
                          data-testid={`split-badge-${s.id}`}
                          title="TurfPulse runs each allowed day with a different duration based on the dry-gap interval — lighter before short gaps, deeper before long gaps. Encourages deeper roots."
                        >
                          ✨ Smart Split
                        </Badge>
                      )}
                      {s.split_strategy === 'even' && (
                        <Badge
                          variant="outline"
                          className="text-[9px] uppercase text-zinc-500 border-zinc-600"
                          data-testid={`split-badge-${s.id}`}
                          title="Every allowed day uses the same runtime — like a vanilla Rachio schedule."
                        >
                          Even Split
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {(s.days_of_week || []).map(dayLabel).join(' · ')} at {s.start_time_local} ·{' '}
                      {s.zones?.length || 0} zones · ~{totalMin} min
                    </div>
                    {/* Per-day breakdown — surfaces the Smart Split math
                        (Tue: light vs Thu: deep) so users see exactly how
                        their weekly water gets redistributed across days. */}
                    {breakdowns[s.id] && breakdowns[s.id].length > 1 && (
                      <div
                        className="mt-2 flex flex-wrap gap-1.5"
                        data-testid={`split-breakdown-${s.id}`}
                      >
                        {breakdowns[s.id].map((b) => {
                          const styleClass =
                            b.style === 'deep'
                              ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40'
                              : b.style === 'light'
                              ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40'
                              : 'bg-muted text-foreground/80 border-border';
                          const styleLabel =
                            b.style === 'deep'
                              ? 'deep soak'
                              : b.style === 'light'
                              ? 'light top-up'
                              : 'balanced';
                          return (
                            <span
                              key={b.day_of_week}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border tabular-nums ${styleClass}`}
                              title={`${b.day_name}: ${b.total_minutes} min · ${styleLabel} · ${b.gap_after_days}-day gap after`}
                            >
                              <span className="font-semibold">{b.day_name}</span>
                              <span>{b.total_minutes} min</span>
                              {b.style !== 'even' && b.style !== 'balanced' && (
                                <span className="opacity-70">· {styleLabel}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-1.5">
                      Next: {next}
                      {!guardian.autoFire && (
                        <span className="text-amber-600 dark:text-amber-400 font-medium"> · 🛡️ advisory · won't auto-fire</span>
                      )}
                      {s.last_skip_reason && (
                        <span className="text-amber-500"> · last skip: {s.last_skip_reason}</span>
                      )}
                      {s.fire_count > 0 && <span> · {s.fire_count} runs</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggle(s)}
                      disabled={busy[s.id]}
                      data-testid={`recurring-toggle-${s.id}`}
                    >
                      {s.enabled ? 'Pause' : 'Resume'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => remove(s)}
                      disabled={busy[s.id]}
                      data-testid={`recurring-delete-${s.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
